// context/SnackbarProvider.tsx
import React, {useState, createContext, useContext} from 'react';
import {Snackbar, Alert, AlertColor} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

type SnackbarVariant = 'snackbar' | 'banner';
type SnackbarType = 'success' | 'error' | 'info';

interface SnackbarOptions {
  message: string;
  type?: SnackbarType;
  variant?: SnackbarVariant;
  actions?: React.ReactNode;
  duration?: number;
  icon?: string;
  bgColor?: string;
}

interface SnackbarContextValue {
  showSnackbar: (options: SnackbarOptions) => void;
}

const iconMap = {
  success: <CheckIcon sx={{color: 'white', fontSize: 35}} />,
  error: <PriorityHighIcon sx={{color: 'white', fontSize: 35}} />,
  info: <InfoOutlineIcon sx={{color: 'white', fontSize: 35}} />,
};

const colorMap = {
  success: '#00A906',
  error: '#C80034',
  info: '#007AFF',
};

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

const SnackbarContextProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [queue, setQueue] = useState<SnackbarOptions[]>([]);
  const [current, setCurrent] = useState<SnackbarOptions | null>(null);
  const [open, setOpen] = useState(false);

  const showSnackbar = (options: SnackbarOptions) => {
    setQueue(prev => [...prev, options]);
  };

  // Pull the next queued toast in once the previous one has fully closed,
  // so simultaneous calls (e.g. success + error) are shown one after another
  // instead of one clobbering the other's state.
  React.useEffect(() => {
    if (open || current || queue.length === 0) return;
    setCurrent(queue[0]);
    setQueue(prev => prev.slice(1));
    setOpen(true);
  }, [open, current, queue]);

  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    if (!open) {
      if (!current) return;
      const clearTimer = setTimeout(() => setCurrent(null), 200);
      return () => clearTimeout(clearTimer);
    }

    const timer = setTimeout(() => {
      setOpen(false);
    }, current?.duration ?? 3000);

    return () => clearTimeout(timer);
  }, [open, current]);

  const {
    message = '',
    type = 'info',
    variant = 'snackbar',
    actions,
    duration = 3000,
    icon = null,
    bgColor = 'white',
  } = current ?? {};

  return (
    <SnackbarContext.Provider value={{showSnackbar}}>
      {children}
      {variant === 'snackbar' && (
        <Snackbar
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          open={open}
          autoHideDuration={duration}
          onClose={handleClose}
          sx={{zIndex: 9999}}>
          <Alert
            severity={type as AlertColor}
            icon={iconMap[type]}
            action={actions}
            sx={{
              minWidth: {
                lg: '820px',
                md: '600px',
                sm: '400px',
                xs: '300px',
              },
              justifyContent: 'center',
              alignItems: 'center',
              color: '#fff',
              fontSize: 24,
              fontWeight: 500,
              backgroundColor: colorMap[type],
            }}>
            {message}
          </Alert>
        </Snackbar>
      )}
      {variant === 'banner' && open && (
        <div className="fixed bottom-4 left-1/2 z-[9999] -translate-x-1/2 w-[90%] max-w-[820px]">
          <div
            className={`flex items-center justify-between rounded-xl px-6 py-4       border border-[#19294F]
      shadow-[0px_4px_4px_0px_#00000040]  ${bgColor}`}>
            <div className="flex items-center gap-2">
              {icon && (
                <img
                  src={icon}
                  alt="icon"
                  className="w-[40px] h-[40px] object-contain"
                />
              )}
              <span className="text-[24px] font-medium text-black">
                {message}
              </span>
            </div>
            <div className="flex items-center gap-6">{actions}</div>
          </div>
        </div>
      )}
    </SnackbarContext.Provider>
  );
};

export default SnackbarContextProvider;

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context)
    throw new Error('useSnackbar must be used within SnackbarProvider');
  return context;
};
